import {
  AiAnalysisResultSchema,
  type HrNotification,
  HrNotificationSchema,
  type HrPayoutRequest,
  HrPayoutRequestSchema,
  type HrTransaction,
  HrTransactionSchema,
  type HrUserBalance,
  HrUserBalanceSchema,
  type IbanInput,
  IbanInputSchema,
  type InvitationInput,
  InvitationInputSchema,
  type NotifFilter,
  NotifFilterSchema,
  type PayoutRequest,
  PayoutRequestSchema,
  type PromoInvitation,
  PromoInvitationSchema,
  type Promotion,
  type PromotionProgress,
  PromotionSchema,
  type UserPromotion,
  UserPromotionSchema,
} from '@sazeo/schemas';
import { getTenantClient } from '@sazeo/supabase-tenant';
import { buildFunctionUrl } from './functionUrl';
import type { PaginatedResult, SupabaseClientLike } from './people';

export type {
  AiAnalysisResult,
  AiStatus,
  HrNotification,
  HrPayoutRequest,
  HrTransaction,
  HrUserBalance,
  IbanInput,
  InvitationInput,
  InvitationStatus,
  NotifFilter,
  NotificationType,
  PayoutRequest,
  PayoutType,
  PromoInvitation,
  Promotion,
  PromotionProgress,
  PromotionRewardType,
  PromotionStatus,
  UserPromotion,
} from '@sazeo/schemas';

type DbClient = SupabaseClientLike;

function db(): DbClient {
  return getTenantClient() as unknown as DbClient;
}

async function currentUserId(client: DbClient = db()): Promise<string> {
  const { data } = await client.auth.getUser();
  const id = data.user?.id;
  if (!id) throw new Error('Authenticated user is required');
  return id;
}

async function authHeaders(client: DbClient = db()): Promise<Record<string, string>> {
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function edgeJson<T>(path: string, init?: RequestInit, client: DbClient = db()): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('content-type', headers.get('content-type') ?? 'application/json');
  headers.set('x-request-id', headers.get('x-request-id') ?? crypto.randomUUID());
  for (const [key, value] of Object.entries(await authHeaders(client))) {
    headers.set(key, value);
  }

  const res = await fetch(buildFunctionUrl(path), { ...init, headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function row<T>(data: T | null, error: { message?: string } | null | undefined): T {
  if (error) throw new Error(error.message ?? 'Database error');
  if (!data) throw new Error('Record not found');
  return data;
}

function rows<T>(data: T[] | null, error: { message?: string } | null | undefined): T[] {
  if (error) throw new Error(error.message ?? 'Database error');
  return data ?? [];
}

function parseMoney(value: number | string | null | undefined): number {
  return Number(value ?? 0);
}

function emptyBalance(userId: string): HrUserBalance {
  return {
    earned_promotions: 0,
    iban: null,
    iban_bank_name: null,
    iban_holder_name: null,
    iban_verified: false,
    pending_balance: 0,
    total_balance: 0,
    updated_at: new Date(0).toISOString(),
    user_id: userId,
    withdrawn_total: 0,
  };
}

// Catalog
export async function getPromotions(tenantId: string): Promise<Promotion[]> {
  void tenantId;
  const { data, error } = await db()
    .from('promotions')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  return rows(data, error).map((item) => PromotionSchema.parse(item));
}

export async function getPromotion(slug: string): Promise<Promotion> {
  const { data, error } = await db()
    .from('promotions')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .single();
  return PromotionSchema.parse(row(data, error));
}

// User participation
export async function startPromotion(
  tenantId: string,
  promotionId: string,
): Promise<UserPromotion> {
  const client = db();
  const userId = await currentUserId(client);
  const { data: promotion, error: promotionError } = await client
    .from('promotions')
    .select('id,duration_days,reward_amount')
    .eq('id', promotionId)
    .eq('is_active', true)
    .single();
  const promo = row(
    promotion as { duration_days: number; id: string; reward_amount: number | string } | null,
    promotionError,
  );
  const expiresAt = new Date(Date.now() + promo.duration_days * 86_400_000).toISOString();
  const { data, error } = await client
    .from('user_promotions')
    .insert({
      expires_at: expiresAt,
      promotion_id: promotionId,
      tenant_id: tenantId,
      user_id: userId,
    })
    .select('*')
    .single();
  return UserPromotionSchema.parse(row(data, error));
}

export async function getMyPromotions(tenantId: string): Promise<UserPromotion[]> {
  const userId = await currentUserId();
  const { data, error } = await db()
    .from('user_promotions')
    .select('*,promotions(*)')
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  return rows(data, error).map((item) => UserPromotionSchema.parse(item));
}

export async function getPromotionProgress(userPromotionId: string): Promise<PromotionProgress> {
  const client = db();
  const { data: participation, error: participationError } = await client
    .from('user_promotions')
    .select('*,promotions(*)')
    .eq('id', userPromotionId)
    .single();
  const userPromotion = UserPromotionSchema.parse(row(participation, participationError));

  const { data: invitations, error: invitationError } = await client
    .from('promo_invitations')
    .select('*')
    .eq('user_promotion_id', userPromotionId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });
  const invitees = rows(dataOrEmpty(invitations), invitationError).map((item) =>
    PromoInvitationSchema.parse(item),
  );

  return {
    completed_count: invitees.filter((invitee) => invitee.status === 'registered').length,
    invitees,
    required_count: userPromotion.promotions?.required_count ?? invitees.length,
    user_promotion: userPromotion,
  };
}

function dataOrEmpty<T>(data: T[] | null): T[] {
  return data ?? [];
}

// Invitations
export async function addInvitation(
  userPromotionId: string,
  data: InvitationInput,
): Promise<PromoInvitation> {
  const client = db();
  const userId = await currentUserId(client);
  const input = InvitationInputSchema.parse(data);

  const { data: participation, error: participationError } = await client
    .from('user_promotions')
    .select('id,tenant_id,user_id,expires_at,change_count')
    .eq('id', userPromotionId)
    .single();
  const userPromotion = row(
    participation as {
      change_count: number;
      expires_at: string;
      id: string;
      tenant_id: string;
      user_id: string;
    } | null,
    participationError,
  );
  if (userPromotion.change_count > 10) {
    throw new Error('Invitation change limit exceeded');
  }

  const analysis = input.ai_analysis_result
    ? AiAnalysisResultSchema.parse(input.ai_analysis_result)
    : null;
  const { data: invitation, error: invitationError } = await client
    .from('promo_invitations')
    .insert({
      ai_analysis_result: analysis,
      ai_confidence_score: analysis?.confidence_score ?? null,
      ai_risk_score: analysis?.risk_score ?? null,
      ai_status: analysis ? (analysis.is_valid_hr ? 'passed' : 'failed') : 'pending',
      expires_at: userPromotion.expires_at,
      invitee_email: input.invitee_email,
      invitee_first_name: input.invitee_first_name,
      invitee_last_name: input.invitee_last_name,
      invitee_linkedin_url: input.invitee_linkedin_url ?? null,
      inviter_id: userId,
      tenant_id: userPromotion.tenant_id,
      user_promotion_id: userPromotionId,
    })
    .select('*')
    .single();

  const created = PromoInvitationSchema.parse(row(invitation, invitationError));
  if (input.screenshot_paths.length > 0) {
    const { error: screenshotsError } = await client.from('invitation_screenshots').insert(
      input.screenshot_paths.map((storage_path) => ({
        invitation_id: created.id,
        storage_path,
        tenant_id: created.tenant_id,
      })),
    );
    if (screenshotsError) throw new Error(screenshotsError.message);
  }
  return created;
}

export async function removeInvitation(invitationId: string): Promise<void> {
  const client = db();
  const { data: invitation, error: invitationError } = await client
    .from('promo_invitations')
    .select('id,status,user_promotion_id')
    .eq('id', invitationId)
    .single();
  const item = row(
    invitation as { id: string; status: string; user_promotion_id: string } | null,
    invitationError,
  );
  if (item.status === 'registered') {
    throw new Error('Registered invitees cannot be removed');
  }

  const { data: participation, error: participationError } = await client
    .from('user_promotions')
    .select('id,change_count')
    .eq('id', item.user_promotion_id)
    .single();
  const userPromotion = row(
    participation as { change_count: number; id: string } | null,
    participationError,
  );
  if (userPromotion.change_count >= 10) {
    throw new Error('Invitation change limit exceeded');
  }

  const { error: updateInvitationError } = await client
    .from('promo_invitations')
    .update({ deleted_at: new Date().toISOString(), status: 'cancelled' })
    .eq('id', invitationId);
  if (updateInvitationError) throw new Error(updateInvitationError.message);

  const { error: updatePromotionError } = await client
    .from('user_promotions')
    .update({ change_count: userPromotion.change_count + 1 })
    .eq('id', item.user_promotion_id);
  if (updatePromotionError) throw new Error(updatePromotionError.message);
}

export async function sendReminder(invitationId: string): Promise<void> {
  await edgeJson('promo-invite-send', {
    body: JSON.stringify({ invitation_id: invitationId, reminder: true }),
    method: 'POST',
  });
}

// Balance
export async function getMyBalance(): Promise<HrUserBalance> {
  const userId = await currentUserId();
  const { data, error } = await db()
    .from('hr_user_balances')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? HrUserBalanceSchema.parse(data) : emptyBalance(userId);
}

export async function getMyTransactions(page = 1): Promise<PaginatedResult<HrTransaction>> {
  const userId = await currentUserId();
  const limit = 25;
  const from = (page - 1) * limit;
  const to = from + limit - 1;
  const { count, data, error } = await db()
    .from('hr_transactions')
    .select('*', { count: 'exact' })
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(from, to);
  if (error) throw new Error(error.message);
  return {
    cursor: count && to + 1 < count ? String(page + 1) : null,
    data: (data ?? []).map((item: unknown) => HrTransactionSchema.parse(item)),
    total: count ?? data?.length ?? 0,
  };
}

export async function saveIban(data: IbanInput): Promise<void> {
  const input = IbanInputSchema.parse(data);
  const userId = await currentUserId();
  const { error } = await db().from('hr_user_balances').upsert({
    iban: input.iban,
    iban_bank_name: input.iban_bank_name,
    iban_holder_name: input.iban_holder_name,
    iban_verified: false,
    updated_at: new Date().toISOString(),
    user_id: userId,
  });
  if (error) throw new Error(error.message);
}

export async function requestPayout(data: PayoutRequest): Promise<HrPayoutRequest> {
  const input = PayoutRequestSchema.parse(data);
  const balance = await getMyBalance();
  if (parseMoney(balance.total_balance) < input.amount) {
    throw new Error('Insufficient balance');
  }
  const payload = await edgeJson<{ data?: unknown } | unknown>('promo-payout-init', {
    body: JSON.stringify(input),
    method: 'POST',
  });
  return HrPayoutRequestSchema.parse(
    payload && typeof payload === 'object' && 'data' in payload
      ? (payload as { data: unknown }).data
      : payload,
  );
}

// Notifications
export async function getMyNotifications(filter: NotifFilter = {}): Promise<HrNotification[]> {
  const input = NotifFilterSchema.parse(filter);
  const userId = await currentUserId();
  let query = db()
    .from('hr_notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(input.limit ?? 50);
  if (input.unread_only) query = query.eq('is_read', false);
  if (input.type) query = query.eq('type', input.type);
  const { data, error } = await query;
  return rows(data, error).map((item) => HrNotificationSchema.parse(item));
}

export async function getUnreadCount(): Promise<number> {
  const userId = await currentUserId();
  const { count, error } = await db()
    .from('hr_notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await db()
    .from('hr_notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId);
  if (error) throw new Error(error.message);
}

export async function markAllAsRead(): Promise<void> {
  const userId = await currentUserId();
  const { error } = await db()
    .from('hr_notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('is_read', false);
  if (error) throw new Error(error.message);
}

export async function analyzeInviteeScreenshots(
  input: { first_name: string; last_name: string; linkedin_url?: string | null },
  files: File[],
): Promise<import('@sazeo/schemas').AiAnalysisResult> {
  const form = new FormData();
  form.append('data', JSON.stringify(input));
  for (const file of files) form.append('screenshots', file);
  const auth = await authHeaders();
  const res = await fetch(buildFunctionUrl('promo-ai-analyze'), {
    method: 'POST',
    headers: { ...auth, 'x-request-id': crypto.randomUUID() },
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return AiAnalysisResultSchema.parse((await res.json()) as unknown);
}
